import type {
  CreateAutomodRuleBody,
  ModifyMemberVerificationBody,
  ModifyWelcomeScreenBody,
} from '../api/chat-api.js';
import {
  OverwriteType,
  type PermissionDecision,
  type PermissionFlag,
  PermissionFlags,
} from '../contracts/chat-permissions.js';
import type {
  APIAutoModerationRule,
  APIChannel,
  APIOverwrite,
} from '../contracts/chat-types.js';
import { permissionTexts } from '../i18n/index.js';
import { canInGuild } from '../permissoes/index.js';
import type { ChatState, Snowflake } from '../stores/state.js';

/**
 * Regras das telas de administração do servidor (web e celular): grupos de
 * permissões, os três estados da permissão no canal, a explicação do
 * "Verificar acesso", moderação automática, boas-vindas, castigo e banimento.
 */

export type GrupoDePermissoes = { titulo: string; flags: PermissionFlag[] };

/**
 * As permissões que aparecem nas telas, em grupos como no Discord. Voz,
 * webhooks, comandos e apps ficam de fora (fora do escopo).
 */
export const PERMISSOES_DO_SERVIDOR: GrupoDePermissoes[] = [
  {
    titulo: 'Permissões gerais do servidor',
    flags: [
      'VIEW_CHANNEL',
      'MANAGE_CHANNELS',
      'MANAGE_ROLES',
      'CREATE_GUILD_EXPRESSIONS',
      'MANAGE_GUILD_EXPRESSIONS',
      'VIEW_AUDIT_LOG',
      'VIEW_GUILD_INSIGHTS',
      'MANAGE_GUILD',
    ],
  },
  {
    titulo: 'Permissões de membro',
    flags: [
      'CREATE_INSTANT_INVITE',
      'CHANGE_NICKNAME',
      'MANAGE_NICKNAMES',
      'KICK_MEMBERS',
      'BAN_MEMBERS',
      'MODERATE_MEMBERS',
    ],
  },
  {
    titulo: 'Permissões de canal de texto',
    flags: [
      'SEND_MESSAGES',
      'SEND_MESSAGES_IN_THREADS',
      'CREATE_PUBLIC_THREADS',
      'CREATE_PRIVATE_THREADS',
      'EMBED_LINKS',
      'ATTACH_FILES',
      'ADD_REACTIONS',
      'USE_EXTERNAL_EMOJIS',
      'USE_EXTERNAL_STICKERS',
      'MENTION_EVERYONE',
      'MANAGE_MESSAGES',
      'PIN_MESSAGES',
      'BYPASS_SLOWMODE',
      'MANAGE_THREADS',
      'READ_MESSAGE_HISTORY',
      'SEND_POLLS',
    ],
  },
  {
    titulo: 'Permissões de eventos',
    flags: ['CREATE_EVENTS', 'MANAGE_EVENTS'],
  },
];

/** Só no cargo (no canal não existe): o Administrador, no fim da lista. */
export const PERMISSOES_AVANCADAS: GrupoDePermissoes = {
  titulo: 'Permissões avançadas',
  flags: ['ADMINISTRATOR'],
};

/** Nos canais e categorias não entram as que só valem no servidor. */
export const PERMISSOES_DO_CANAL: GrupoDePermissoes[] = [
  {
    titulo: 'Permissões gerais',
    flags: [
      'VIEW_CHANNEL',
      'MANAGE_CHANNELS',
      'MANAGE_ROLES',
      'CREATE_INSTANT_INVITE',
    ],
  },
  {
    titulo: 'Permissões de texto',
    flags: PERMISSOES_DO_SERVIDOR[2].flags,
  },
  {
    titulo: 'Permissões de eventos',
    flags: ['CREATE_EVENTS', 'MANAGE_EVENTS'],
  },
];

export function temPermissao(
  valor: string | bigint,
  flag: PermissionFlag,
): boolean {
  try {
    return (BigInt(valor) & PermissionFlags[flag]) === PermissionFlags[flag];
  } catch {
    return false;
  }
}

/** O bitfield com a permissão ligada ou desligada (em texto, como a API usa). */
export function comPermissao(
  valor: string,
  flag: PermissionFlag,
  ligado: boolean,
): string {
  const atual = BigInt(valor || '0');
  return String(
    ligado ? atual | PermissionFlags[flag] : atual & ~PermissionFlags[flag],
  );
}

/** Nome da permissão; no canal, "Gerenciar cargos" é "Gerenciar permissões" (como o Discord). */
export function nomeDaPermissao(flag: PermissionFlag, noCanal = false): string {
  if (noCanal && flag === 'MANAGE_ROLES') return 'Gerenciar permissões';
  return permissionTexts[flag].name;
}

/** Descrição; o Administrador ganha o aviso (e, no @everyone, o "deixe desligado"). */
export function descricaoDaPermissao(
  flag: PermissionFlag,
  noEveryone = false,
): string {
  if (flag === 'ADMINISTRATOR') {
    return noEveryone
      ? 'Aqui daria acesso total a todo mundo. Deixe desligado.'
      : 'Todas as permissões, em todos os canais. Administrador ignora as permissões de canal e dá acesso a tudo. Conceda com cuidado.';
  }
  return permissionTexts[flag].description;
}

/** Os grupos com as permissões cujo nome ou descrição têm o termo (vazio = todas). */
export function filtrarPermissoes(
  grupos: GrupoDePermissoes[],
  termo: string,
  noCanal = false,
): GrupoDePermissoes[] {
  const busca = termo.trim().toLowerCase();
  if (!busca) return grupos;
  return grupos
    .map((grupo) => ({
      ...grupo,
      flags: grupo.flags.filter(
        (flag) =>
          nomeDaPermissao(flag, noCanal).toLowerCase().includes(busca) ||
          descricaoDaPermissao(flag).toLowerCase().includes(busca),
      ),
    }))
    .filter((grupo) => grupo.flags.length);
}

/** Estado de uma permissão no canal para um cargo ou membro. */
export type EstadoNoCanal = 'negar' | 'herdar' | 'permitir';

export function estadoNoCanal(
  overwrite: APIOverwrite | undefined,
  flag: PermissionFlag,
): EstadoNoCanal {
  if (!overwrite) return 'herdar';
  const bit = PermissionFlags[flag];
  try {
    if ((BigInt(overwrite.deny) & bit) !== 0n) return 'negar';
    if ((BigInt(overwrite.allow) & bit) !== 0n) return 'permitir';
  } catch {
    return 'herdar';
  }
  return 'herdar';
}

/** Muda um bit do overwrite (cria o overwrite se precisar; vazio continua na lista). */
export function mudarNoCanal(
  lista: APIOverwrite[],
  alvo: { id: Snowflake; type: number },
  flag: PermissionFlag,
  estado: EstadoNoCanal,
): APIOverwrite[] {
  const atual = lista.find((item) => item.id === alvo.id) ?? {
    id: alvo.id,
    type: alvo.type,
    allow: '0',
    deny: '0',
  };
  const bit = PermissionFlags[flag];
  let allow = BigInt(atual.allow) & ~bit;
  let deny = BigInt(atual.deny) & ~bit;
  if (estado === 'permitir') allow |= bit;
  if (estado === 'negar') deny |= bit;
  const novo = { ...atual, allow: String(allow), deny: String(deny) };
  return lista.some((item) => item.id === alvo.id)
    ? lista.map((item) => (item.id === alvo.id ? novo : item))
    : [...lista, novo];
}

/** "Canal privado": o @everyone com Ver canal negado. */
export function canalPrivadoNaLista(
  overwrites: APIOverwrite[],
  guildId: Snowflake,
): boolean {
  return (
    estadoNoCanal(
      overwrites.find((item) => item.id === guildId),
      'VIEW_CHANNEL',
    ) === 'negar'
  );
}

/**
 * Quem entra na lista de um canal privado já ganha Ver canal (como o
 * Discord); num canal aberto, entra sem nada marcado.
 */
export function novoAlvoNoCanal(
  overwrites: APIOverwrite[],
  guildId: Snowflake,
  alvo: { id: Snowflake; type: number },
): APIOverwrite {
  return {
    id: alvo.id,
    type: alvo.type,
    allow: canalPrivadoNaLista(overwrites, guildId)
      ? String(PermissionFlags.VIEW_CHANNEL)
      : '0',
    deny: '0',
  };
}

/** Canal ou categoria criado já privado: o @everyone sem ver, os cargos escolhidos com ver. */
export function permissoesDeCanalPrivado(
  guildId: Snowflake,
  cargos: Snowflake[],
): Array<{ id: Snowflake; type: 0; allow: string; deny: string }> {
  const ver = String(PermissionFlags.VIEW_CHANNEL);
  return [
    { id: guildId, type: 0, allow: '0', deny: ver },
    ...cargos.map((id) => ({ id, type: 0 as const, allow: ver, deny: '0' })),
  ];
}

/** As permissões do canal batem com as da categoria? */
export function permissoesSincronizadas(
  canal: APIChannel,
  categoria: APIChannel | undefined,
): boolean {
  if (!categoria) return true;
  const chave = (lista: APIOverwrite[] | undefined) =>
    JSON.stringify(
      [...(lista ?? [])]
        .filter((item) => item.allow !== '0' || item.deny !== '0')
        .map((item) => [item.id, item.type, item.allow, item.deny])
        .sort(),
    );
  return (
    chave(canal.permission_overwrites) ===
    chave(categoria.permission_overwrites)
  );
}

/** Ordem da lista de alvos do canal: @everyone, depois cargos (do mais alto) e membros. */
export function alvosDoCanal(
  overwrites: APIOverwrite[],
  guildId: Snowflake,
  posicaoDoCargo: (id: Snowflake) => number | undefined,
): Array<{ id: Snowflake; type: number }> {
  return [
    { id: guildId, type: OverwriteType.ROLE },
    ...overwrites
      .filter((item) => item.id !== guildId)
      .map((item) => ({
        id: item.id,
        type: item.type,
        posicao:
          item.type === OverwriteType.ROLE
            ? (posicaoDoCargo(item.id) ?? -1)
            : -1,
      }))
      .sort((a, b) => b.posicao - a.posicao)
      .map(({ id, type }) => ({ id, type })),
  ];
}

/** A frase do "Verificar acesso": por que pode (ou não). */
export function explicarDecisao(
  decisao: PermissionDecision | null,
  nomeCargo: (id: Snowflake) => string,
): string {
  if (!decisao) return '';
  switch (decisao.by) {
    case 'owner':
      return 'é o dono do servidor';
    case 'administrator':
      return 'tem um cargo de Administrador';
    case 'timeout':
      return 'está de castigo';
    case 'pending':
      return 'ainda não aceitou as regras';
    case 'no-view-channel':
      return 'não vê o canal';
    case 'no-send-messages':
      return 'não pode enviar mensagens aqui';
    case 'member-overwrite':
      return decisao.allowed
        ? 'liberado para a própria pessoa neste canal'
        : 'negado para a própria pessoa neste canal';
    case 'role-overwrite': {
      const quem = decisao.roleIds.map(nomeCargo).join(', ');
      const sem = decisao.withoutExplicitAllow?.length
        ? `. ${decisao.withoutExplicitAllow.map(nomeCargo).join(', ')} não permite explicitamente aqui, então a negação vence`
        : '';
      return decisao.allowed
        ? `liberado pela permissão de ${quem} neste canal`
        : `negado pela permissão de ${quem} neste canal${sem}`;
    }
    case 'everyone-overwrite':
      return decisao.allowed
        ? 'liberado para todos neste canal'
        : 'negado para todos (@everyone) neste canal';
    case 'roles':
      return decisao.allowed
        ? `pelo cargo ${decisao.roleIds.map(nomeCargo).join(', ')}`
        : 'nenhum cargo dá essa permissão no servidor';
  }
}

/** O que o "Verificar acesso" confere. */
export const VERIFICAR_ACESSO: Array<[string, PermissionFlag]> = [
  ['Ver o canal e ler o histórico', 'VIEW_CHANNEL'],
  ['Enviar mensagens', 'SEND_MESSAGES'],
];

/** As cores do seletor do Discord. */
export const CORES_DE_CARGO = [
  0x1abc9c, 0x2ecc71, 0x3498db, 0x9b59b6, 0xe91e63, 0xf1c40f, 0xe67e22,
  0xe74c3c, 0x95a5a6, 0x607d8b, 0x11806a, 0x1f8b4c, 0x206694, 0x71368a,
  0xad1457, 0xc27c0e, 0xa84300, 0x992d22, 0x979c9f, 0x546e7a,
];

/** Modo lento (`rate_limit_per_user`), como as opções do Discord. */
export const MODO_LENTO: Array<[number, string]> = [
  [0, 'Desligado'],
  [5, '5 s'],
  [10, '10 s'],
  [15, '15 s'],
  [30, '30 s'],
  [60, '1 min'],
  [120, '2 min'],
  [300, '5 min'],
  [600, '10 min'],
  [900, '15 min'],
  [1800, '30 min'],
  [3600, '1 h'],
  [7200, '2 h'],
  [21600, '6 h'],
];

/** Ocultar threads após inatividade (`default_auto_archive_duration`, minutos). */
export const ARQUIVAR_THREADS: Array<[60 | 1440 | 4320 | 10080, string]> = [
  [60, '1 hora'],
  [1440, '24 horas'],
  [4320, '3 dias'],
  [10080, '1 semana'],
];

/** Castigo (segundos), como o Discord oferece. */
export const DURACOES_DE_CASTIGO: Array<[number, string]> = [
  [60, '60 segundos'],
  [300, '5 minutos'],
  [600, '10 minutos'],
  [3600, '1 hora'],
  [86400, '1 dia'],
  [604800, '1 semana'],
];

/** Banir: quanto do histórico da pessoa apagar (`delete_message_seconds`). */
export const APAGAR_AO_BANIR: Array<[number, string]> = [
  [0, 'Não apagar'],
  [3600, 'Última hora'],
  [21600, 'Últimas 6 horas'],
  [43200, 'Últimas 12 horas'],
  [86400, 'Últimas 24 horas'],
  [259200, 'Últimos 3 dias'],
  [604800, 'Últimos 7 dias'],
];

/** Ações de moderação na auditoria (o histórico do membro). */
export const ACOES_DE_MODERACAO: Record<number, string> = {
  20: 'Expulso',
  22: 'Banido',
  23: 'Banimento revogado',
  24: 'Alterado',
  25: 'Cargos alterados',
};

/** Bit de `system_channel_flags`: não avisar quando alguém entra. */
export const SEM_AVISO_DE_ENTRADA = 1 << 0;

export const MAX_EMOJIS = 250;
export const MAX_FIGURINHAS = 30;
export const LIMITE_DO_EMOJI = 256 * 1024;
export const LIMITE_DA_FIGURINHA = 512 * 1024;
export const NOME_DE_EMOJI = /^[A-Za-z0-9_]{2,32}$/;

/** Nome de emoji a partir do nome do arquivo (sem extensão, só letras, números e _). */
export function nomeDeEmojiDoArquivo(arquivo: string): string {
  return arquivo
    .replace(/\.[^.]+$/, '')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .slice(0, 32);
}

/** Nome do canal como o Discord grava: minúsculo, espaços viram hífen (categoria fica como está). */
export function nomeDeCanal(nome: string, categoria = false): string {
  const limpo = nome.trim();
  return categoria ? limpo : limpo.toLowerCase().replace(/\s+/g, '-');
}

// --- Moderação automática --------------------------------------------------

export const REGRA_DE_PALAVRAS = 1;
export const REGRA_DE_MENCOES = 5;

export type RascunhoDeRegra = {
  tipo: typeof REGRA_DE_PALAVRAS | typeof REGRA_DE_MENCOES;
  nome: string;
  /** Uma por linha (ou separadas por vírgula). */
  palavras: string;
  padroes: string;
  limite: number;
  bloquear: boolean;
  mensagem: string;
  /** Canal do alerta; vazio = não alerta. */
  alerta: string;
  /** Segundos de castigo; 0 = não castiga. */
  castigo: number;
  isentos: Snowflake[];
};

export const CASTIGO_DA_REGRA: Array<[number, string]> = [
  [0, 'Não castigar'],
  [60, 'Por 60 segundos'],
  [300, 'Por 5 minutos'],
  [600, 'Por 10 minutos'],
  [3600, 'Por 1 hora'],
];

/** O que a regra faz (para o resumo na lista). */
export function acoesDaRegra(regra: APIAutoModerationRule) {
  return {
    bloqueia: regra.actions.some((acao) => acao.type === 1),
    mensagem:
      (regra.actions.find((acao) => acao.type === 1)?.metadata
        ?.custom_message as string | undefined) ?? '',
    alerta:
      (regra.actions.find((acao) => acao.type === 2)?.metadata?.channel_id as
        string | undefined) ?? null,
    castigo:
      (regra.actions.find((acao) => acao.type === 3)?.metadata
        ?.duration_seconds as number | undefined) ?? null,
  };
}

export function rascunhoDaRegra(
  regra: APIAutoModerationRule | null,
): RascunhoDeRegra {
  const acoes = regra ? acoesDaRegra(regra) : null;
  return {
    tipo:
      regra?.trigger_type === REGRA_DE_MENCOES
        ? REGRA_DE_MENCOES
        : REGRA_DE_PALAVRAS,
    nome: regra?.name ?? '',
    palavras: (regra?.trigger_metadata.keyword_filter ?? []).join('\n'),
    padroes: (regra?.trigger_metadata.regex_patterns ?? []).join('\n'),
    limite: regra?.trigger_metadata.mention_total_limit ?? 10,
    bloquear: acoes ? acoes.bloqueia : true,
    mensagem: acoes?.mensagem ?? '',
    alerta: acoes?.alerta ?? '',
    castigo: acoes?.castigo ?? 0,
    isentos: regra?.exempt_roles ?? [],
  };
}

function listaDeTexto(texto: string): string[] {
  return texto
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** O corpo da regra; `null` quando nenhuma ação foi escolhida. */
export function corpoDaRegra(
  rascunho: RascunhoDeRegra,
  ligada = true,
): CreateAutomodRuleBody | null {
  const acoes: CreateAutomodRuleBody['actions'] = [
    ...(rascunho.bloquear
      ? [
          {
            type: 1 as const,
            ...(rascunho.mensagem.trim()
              ? { metadata: { custom_message: rascunho.mensagem.trim() } }
              : {}),
          },
        ]
      : []),
    ...(rascunho.alerta
      ? [{ type: 2 as const, metadata: { channel_id: rascunho.alerta } }]
      : []),
    ...(rascunho.castigo
      ? [
          {
            type: 3 as const,
            metadata: { duration_seconds: rascunho.castigo },
          },
        ]
      : []),
  ];
  if (!acoes.length) return null;
  return {
    name: rascunho.nome.trim(),
    event_type: 1,
    trigger_type: rascunho.tipo,
    trigger_metadata:
      rascunho.tipo === REGRA_DE_PALAVRAS
        ? {
            keyword_filter: listaDeTexto(rascunho.palavras),
            regex_patterns: listaDeTexto(rascunho.padroes),
          }
        : {
            mention_total_limit: Math.min(50, Math.max(1, rascunho.limite)),
          },
    actions: acoes,
    exempt_roles: rascunho.isentos,
    enabled: ligada,
  };
}

// --- Boas-vindas e regras ----------------------------------------------------

/** O "Member Verification" do Discord: pedir aceite das regras na entrada. */
export function corpoDaVerificacao(
  pedir: boolean,
  regras: string[],
): ModifyMemberVerificationBody {
  const limpas = regras.map((item) => item.trim()).filter(Boolean);
  return {
    enabled: pedir,
    form_fields: limpas.length
      ? [
          {
            field_type: 'TERMS',
            label: 'Li e concordo com as regras',
            values: limpas,
            required: true,
          },
        ]
      : [],
  };
}

/** A "Welcome Screen": os canais para quem chega e o texto do alto. */
export function corpoDaTelaDeBoasVindas(
  canais: Array<{ channel_id: Snowflake; description: string }>,
  texto: string,
): ModifyWelcomeScreenBody {
  const escolhidos = canais.filter((item) => item.channel_id);
  return {
    enabled: escolhidos.length > 0 || !!texto.trim(),
    description: texto.trim() || null,
    welcome_channels: escolhidos,
  };
}

// --- O que a pessoa pode mexer no servidor -----------------------------------

export type PodeNoServidor = {
  dono: boolean;
  geral: boolean;
  cargos: boolean;
  expressoes: boolean;
  canais: boolean;
  membros: boolean;
  castigar: boolean;
  expulsar: boolean;
  banir: boolean;
  auditoria: boolean;
  estatisticas: boolean;
};

/** Cada seção das configurações do servidor e a permissão que ela pede (como o Discord). */
export function podeNoServidor(
  estado: ChatState,
  guildId: Snowflake,
): PodeNoServidor {
  const pode = (flag: bigint) => canInGuild(estado, guildId, flag);
  return {
    dono: estado.guilds[guildId]?.owner_id === estado.me?.id,
    geral: pode(PermissionFlags.MANAGE_GUILD),
    cargos: pode(PermissionFlags.MANAGE_ROLES),
    expressoes:
      pode(PermissionFlags.MANAGE_GUILD_EXPRESSIONS) ||
      pode(PermissionFlags.CREATE_GUILD_EXPRESSIONS),
    canais: pode(PermissionFlags.MANAGE_CHANNELS),
    membros:
      pode(PermissionFlags.MANAGE_GUILD) ||
      pode(PermissionFlags.KICK_MEMBERS) ||
      pode(PermissionFlags.BAN_MEMBERS) ||
      pode(PermissionFlags.MODERATE_MEMBERS) ||
      pode(PermissionFlags.MANAGE_ROLES),
    castigar: pode(PermissionFlags.MODERATE_MEMBERS),
    expulsar: pode(PermissionFlags.KICK_MEMBERS),
    banir: pode(PermissionFlags.BAN_MEMBERS),
    auditoria: pode(PermissionFlags.VIEW_AUDIT_LOG),
    estatisticas:
      pode(PermissionFlags.MANAGE_GUILD) ||
      pode(PermissionFlags.VIEW_GUILD_INSIGHTS),
  };
}
